import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Device, type Call } from '@twilio/voice-sdk';

import { getTwilioToken } from '@services/Common';
import { useTwilioStore } from '@store/twilio/useTwilioStore';
import type { UseTwilioVoiceReturn, TwilioStatus } from '@types';
import { writeWarningLog } from '@lib/logger/logWarning.ts';
import { writeInfoLog } from '@lib/logger/logInfo.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';

function toE164 (raw: string, defaultCountryCode = '+1'): string {
  const v = (raw ?? '').replace(/[^\d+]/g, '');
  if (v.length === 0) {
    return '';
  }
  if (v.startsWith('+')) {
    return v;
  }
  if (v.length === 10) {
    return `${defaultCountryCode}${v}`;
  }
  return v.startsWith('00') ? `+${v.slice(2)}` : `${defaultCountryCode}${v}`;
}

function looksLikePhoneNumber (value: string): boolean {
  return /^[+\d][\d\s-]+$/.test(value);
}

interface MaybeCallWithSid {
  parameters?: { CallSid?: string; Params?: string };
  customParameters?: { get?: (k: string) => string | undefined };
}

function extractCallSid (call: Call): string | null {
  const c = call as unknown as MaybeCallWithSid;
  return c.parameters?.CallSid ?? c.customParameters?.get?.('CallSid') ?? null;
}

function getErrMsg (err: unknown, fallback = 'Unexpected error'): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') {
      return m;
    }
  }
  return fallback;
}

const REGISTER_TIMEOUT_MS = 15000;

export function useTwilioVoice (identity?: string): UseTwilioVoiceReturn {
  const deviceRef = useRef<Device | null>(null);
  const connectionRef = useRef<Call | null>(null);
  const registerTimerRef = useRef<number | null>(null);

  const setStatus = useTwilioStore((s) => s.setStatus);
  const setActiveCall = useTwilioStore((s) => s.setActiveCall);
  const setCall = useTwilioStore((s) => s.setCall);
  const setIsMuted = useTwilioStore((s) => s.setIsMuted);
  const setCallSid = useTwilioStore((s) => s.setCallSid);
  const setConferenceName = useTwilioStore((s) => s.setConferenceName);

  const [status, setLocalStatus] = useState<TwilioStatus>('uninitialized');
  const [error, setError] = useState<string | null>(null);

  const updateStatus = (st: TwilioStatus): void => {
    setLocalStatus(st);
    setStatus(st);
  };

  useEffect(() => {
    let mounted = true;

    if (identity == null || identity.trim() === '') {
      void writeWarningLog('Missing identity, skipping Twilio init', 'useTwilioVoice/init', { identity: String(identity ?? '') });
      updateStatus('offline');
      return;
    }

    void (async (): Promise<void> => {
      try {
        updateStatus('registering');
        setError(null);

        const { data, error: svcErr } = await getTwilioToken(identity);
        if (svcErr != null || data?.token == null) {
          throw new Error(svcErr?.message ?? 'Error getting Twilio token');
        }

        void writeInfoLog('Twilio token obtained', 'useTwilioVoice/token', { hasIdentity: true });

        const dev = new Device(data.token, { logLevel: 1 });
        deviceRef.current = dev;

        const markReady = (): void => {
          if (!mounted) {
            return;
          }
          setError(null);
          // The SDK re-emits `registered` after a signaling reconnect or a
          // BFCache restore; don't flip a live call's status back to ready.
          if (useTwilioStore.getState().hasActiveCall) {
            return;
          }
          updateStatus('ready');
          void writeInfoLog('Twilio ready', 'useTwilioVoice/ready', {});
        };

        const clearRegisterTimer = (): void => {
          if (registerTimerRef.current != null) {
            window.clearTimeout(registerTimerRef.current);
            registerTimerRef.current = null;
          }
        };

        const refreshToken = async (): Promise<void> => {
          if (!mounted || identity == null || identity.trim() === '') {
            return;
          }
          try {
            const { data: freshData, error: refreshErr } = await getTwilioToken(identity);
            if (refreshErr != null || freshData?.token == null) {
              void writeWarningLog('Token refresh failed: no token returned', 'useTwilioVoice/tokenRefresh', {});
              return;
            }
            deviceRef.current?.updateToken(freshData.token);
            void writeInfoLog('Twilio token refreshed', 'useTwilioVoice/tokenRefresh', {});
          } catch (e) {
            void writeWarningLog(getErrMsg(e, 'Token refresh error'), 'useTwilioVoice/tokenRefresh', {});
          }
        };

        dev.on('registered', () => {
          clearRegisterTimer();
          markReady();
        });

        dev.on('incoming', (call: Call) => {
          // Extract conference name from ALL incoming calls (duplicates may carry it too)
          const callWithParams = call as unknown as MaybeCallWithSid;
          let confName: string | null = null;
          try {
            const paramsStr = callWithParams.parameters?.Params ?? '';
            if (paramsStr !== '') {
              const parsed = new URLSearchParams(paramsStr);
              confName = parsed.get('conferenceFriendlyName');
            }
          } catch {
            // ignore parse errors
          }
          if (confName == null || confName === '') {
            confName = callWithParams.customParameters?.get?.('conferenceFriendlyName') ?? null;
          }
          if (confName != null && confName !== '') {
            setConferenceName(confName);
          }

          // Guard: if there's already an active call, this is a duplicate incoming event.
          // Do NOT accept (avoids second audio path) and do NOT reject (avoids killing
          // a legitimate conference leg). The ignored call will time out on Twilio's side.
          const existingCall = useTwilioStore.getState().activeCall;
          if (existingCall != null) {
            void writeInfoLog('Duplicate incoming call, ignoring', 'useTwilioVoice/incoming', { conferenceName: confName });
            return;
          }

          connectionRef.current = call;
          setActiveCall(true);
          setCall(call);
          setIsMuted(false);

          void writeInfoLog('Incoming call', 'useTwilioVoice/incoming', { conferenceName: confName });

          try {
            call.accept();
          } catch (e) {
            void writeWarningLog(getErrMsg(e, 'Auto-accept failed'), 'useTwilioVoice/incoming/accept', {});
          }

          call.on('accept', () => {
            const sid = extractCallSid(call);
            if (sid != null) {
              setCallSid(sid);
            }
            void writeInfoLog('Incoming call accepted', 'useTwilioVoice/incoming/accepted', {});
          });

          const resetIncoming = (): void => {
            // Only reset store if THIS call is still the active one.
            // Prevents duplicate incoming calls from wiping state of the real call.
            const current = useTwilioStore.getState().activeCall;
            if (current != null && current !== call) {
              void writeInfoLog('Skipping reset for non-active call', 'useTwilioVoice/incoming/reset', {});
              return;
            }
            setActiveCall(false);
            setCall(null);
            setIsMuted(false);
            setCallSid(null);
            // Cleared here too. endCall() already did it, but a leg that simply
            // disconnects never goes through endCall, and leaving a dead
            // conference name in the store is what the transfer detection keys on.
            setConferenceName(null);
            updateStatus('ready');
            void writeInfoLog('Incoming call ended', 'useTwilioVoice/incoming/reset', {});
          };

          call.on('disconnect', resetIncoming);
          call.on('cancel', resetIncoming);
          call.on('reject', resetIncoming);
          call.on('error', resetIncoming);

          // Sync mute state with Twilio SDK
          call.on('mute', (isMuted: boolean) => {
            setIsMuted(isMuted);
            void writeInfoLog(isMuted ? 'Call muted (SDK event)' : 'Call unmuted (SDK event)', 'useTwilioVoice/incoming/mute', {});
          });
        });

        dev.on('error', (e: unknown) => {
          if (!mounted) {
            return;
          }
          const msg = getErrMsg(e, 'Device error');
          const errCode = typeof e === 'object' && e !== null && 'code' in e
            ? (e as { code?: number }).code
            : undefined;
          // 31000: generic transient error — log but don't surface to user
          if (errCode === 31000) {
            void writeWarningLog(`Twilio transient error: ${msg}`, 'useTwilioVoice/deviceError', { code: errCode });
            return;
          }
          // Token expired — attempt a silent refresh instead of erroring.
          // The SDK surfaces signaling 31205 as AccessTokenExpired (20104).
          if (errCode === 20104 || errCode === 31205) {
            void writeWarningLog('Twilio token expired, refreshing', 'useTwilioVoice/deviceError', { code: errCode });
            void refreshToken();
            return;
          }
          setError(msg);
          updateStatus('error');
          void writeErrorLog(msg, 'useTwilioVoice/deviceError', { status });
        });

        // Proactively refresh token before it expires (~30s before expiry by default)
        dev.on('tokenWillExpire', () => {
          void writeInfoLog('Twilio token will expire, refreshing proactively', 'useTwilioVoice/tokenWillExpire', {});
          void refreshToken();
        });

        registerTimerRef.current = window.setTimeout(() => {
          if (!mounted) {
            return;
          }
          const msg = 'Twilio register timeout';
          setError(msg);
          updateStatus('error');
          void writeErrorLog(msg, 'useTwilioVoice/registerTimeout', { timeoutMs: REGISTER_TIMEOUT_MS });
        }, REGISTER_TIMEOUT_MS);

        void dev.register().catch((e) => {
          clearRegisterTimer();
          if (!mounted) {
            return;
          }
          const msg = getErrMsg(e, 'Device register failed');
          setError(msg);
          updateStatus('error');
          void writeErrorLog(msg, 'useTwilioVoice/register', {});
        });
      } catch (e) {
        if (!mounted) {
          return;
        }
        const msg = getErrMsg(e, 'Setup error');
        setError(msg);
        updateStatus('error');
        void writeErrorLog(msg, 'useTwilioVoice/setup', {});
      }
    })();

    return () => {
      mounted = false;

      if (registerTimerRef.current != null) {
        window.clearTimeout(registerTimerRef.current);
        registerTimerRef.current = null;
      }

      try {
        connectionRef.current?.disconnect();
      } catch (e) {
        void writeWarningLog(getErrMsg(e, 'Disconnect current call failed'), 'useTwilioVoice/cleanup/disconnect', {});
      }

      try {
        deviceRef.current?.disconnectAll();
        deviceRef.current?.destroy();
      } catch (e) {
        void writeWarningLog(getErrMsg(e, 'Destroy device failed'), 'useTwilioVoice/cleanup/device', {});
      }

      connectionRef.current = null;
      deviceRef.current = null;

      setActiveCall(false);
      setCall(null);
      setIsMuted(false);
      setCallSid(null);
      setConferenceName(null);
      updateStatus('offline');

      void writeInfoLog('Cleanup completed', 'useTwilioVoice/cleanup', {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const startCall: UseTwilioVoiceReturn['startCall'] = async (toNumber, params = {}) => {
    const dev = deviceRef.current;
    if (dev == null) {
      throw new Error('Twilio Device not ready');
    }
    if (toNumber.trim() === '') {
      throw new Error('Missing destination');
    }

    updateStatus('busy');

    const destination = looksLikePhoneNumber(toNumber)
      ? toE164(toNumber, '+1')
      : toNumber;

    const strParams: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => {
      strParams[k] = String(v);
    });

    try {
      const call = await dev.connect({
        params: {
          number: destination,
          To: destination,
          ...strParams,
        },
      });

      connectionRef.current = call;
      setCall(call);
      setActiveCall(true);
      setIsMuted(false);

      void writeInfoLog('Outbound call started', 'useTwilioVoice/startCall', { destination });

      // Twilio assigns the CallSid as soon as the call is ringing, well before
      // `accept`. Reading it only on accept left every unanswered, busy or
      // rejected call without a sid, so its disposition had no call log to go
      // to (or, worse, went to the previous call's).
      const publishSid = (): void => {
        const sid = extractCallSid(call);
        if (sid != null && sid.trim() !== '') {
          setCallSid(sid);
        }
      };
      publishSid();
      call.on('ringing', publishSid);

      call.on('accept', () => {
        publishSid();
        void writeInfoLog('Outbound call accepted', 'useTwilioVoice/startCall/accepted', {});
      });

      const reset = (): void => {
        if (connectionRef.current === call) {
          connectionRef.current = null;
        }
        setActiveCall(false);
        setCall(null);
        setIsMuted(false);
        setCallSid(null);
        updateStatus('ready');
        void writeInfoLog('Outbound call ended', 'useTwilioVoice/startCall/reset', {});
      };

      call.on('disconnect', reset);
      call.on('cancel', reset);
      call.on('reject', reset);
      call.on('error', reset);

      // Sync mute state with Twilio SDK
      call.on('mute', (isMuted: boolean) => {
        setIsMuted(isMuted);
        void writeInfoLog(isMuted ? 'Call muted (SDK event)' : 'Call unmuted (SDK event)', 'useTwilioVoice/startCall/mute', {});
      });
    } catch (e) {
      const msg = getErrMsg(e, 'Connect error');
      setError(msg);
      updateStatus('ready');
      void writeErrorLog(msg, 'useTwilioVoice/startCall/error', { destination });
      throw e;
    }
  };

  // Keep the latest endCall impl in a ref so the stable wrapper always calls current logic
  const endCallImplRef = useRef<UseTwilioVoiceReturn['endCall']>(() => {});
  endCallImplRef.current = () => {
    try {
      connectionRef.current?.disconnect();
      void writeInfoLog('End call requested', 'useTwilioVoice/endCall', {});
    } catch (e) {
      void writeWarningLog(getErrMsg(e, 'End call failed'), 'useTwilioVoice/endCall', {});
    }

    try {
      deviceRef.current?.disconnectAll();
      void writeInfoLog('Disconnect all requested', 'useTwilioVoice/endCall/disconnectAll', {});
    } catch (e) {
      void writeWarningLog(getErrMsg(e, 'Disconnect all failed'), 'useTwilioVoice/endCall/disconnectAll', {});
    }

    connectionRef.current = null;
    setActiveCall(false);
    setCall(null);
    setIsMuted(false);
    setCallSid(null);
    setConferenceName(null);
    updateStatus('ready');
  };

  // Stable reference that never changes — avoids unnecessary effect restarts in consumers
  const endCall = useCallback<UseTwilioVoiceReturn['endCall']>(() => {
    endCallImplRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendDigits: UseTwilioVoiceReturn['sendDigits'] = (digits) => {
    try {
      connectionRef.current?.sendDigits(digits);
      void writeInfoLog('DTMF sent', 'useTwilioVoice/sendDigits', { digits });
    } catch (e) {
      void writeWarningLog(getErrMsg(e, 'Send DTMF failed'), 'useTwilioVoice/sendDigits', { digits });
    }
  };

  const currentCall: UseTwilioVoiceReturn['currentCall'] = () => connectionRef.current;

  return useMemo(
    () => ({ status, error, startCall, endCall, sendDigits, currentCall }),
    [status, error],
  );
}
